import { exportMark, pdfColor } from "./export-marks";
import { sourceMeters, scaleSource } from "./source-scale";
import { requireImportTarget } from "./import-target";
import type { FilePlacement } from "./file-import-state";
import { pdfPreview } from "./pdf-preview";
import { isPdfFile } from "../shared/drawing-files";
import {
  PDFDocument,
  StandardFonts,
  rgb,
  pushGraphicsState,
  popGraphicsState,
  moveTo,
  lineTo,
  closePath,
  clip,
  endPath,
} from "pdf-lib";
import { createShapeId } from "@tldraw/tlschema";
import type { Editor } from "./canvas";
import type { Sheet, Mark } from "../shared/schema";
import { drawingExportFooter, drawingExportLayout } from "./export-layout";
import { markLabel } from "./shapes";
import { uploadFile } from "./api";
const pt = 72 / 25.4;
export async function importDrawing(
  editor: Editor,
  room: string,
  file: File,
  page = 1,
  signal?: AbortSignal,
  cloud = false,
  request?: { id: import("@tldraw/tlschema").TLShapeId; placement: FilePlacement },
) {
  signal?.throwIfAborted();
  if (file.size > 30 * 1024 * 1024)
    throw new Error("Please use a file smaller than 30 MB.");
  // Keep the local multipart API and PDF preview on the same type decision.
  if (isPdfFile(file) && file.type !== "application/pdf")
    file = new File([file], file.name, { type: "application/pdf" });
  const source = await uploadFile(room, file, signal, cloud);
  signal?.throwIfAborted();
  let preview = source.src,
    w = 1120,
    h = 792;
  if (isPdfFile(file)) {
    const rendered = await pdfPreview(
      new Uint8Array(await file.arrayBuffer()),
      page,
      signal,
    );
    w = rendered.w;
    h = rendered.h;
    preview = (
      await uploadFile(
        room,
        new File([rendered.blob], `${file.name}-page-${page}.png`, {
          type: "image/png",
        }),
        signal,
        cloud,
      )
    ).src;
  } else {
    if (!source.image) throw new Error("Image dimensions are unavailable.");
    h = (w * source.image.h) / source.image.w;
  }
  if (cloud) preview += "?size=2048";
  signal?.throwIfAborted();
  const sheets = editor
      .getCurrentPageShapes()
      .filter((s) => s.type === "sheet"),
    last = sheets[sheets.length - 1];
  if (editor.getIsReadonly()) throw new Error("Edit access was removed.");
  if (request) requireImportTarget(editor, request.placement);
  const id = request?.id ?? createShapeId();
  if (editor.getShape(id)) return id;
  const center = request
    ? request.placement.parentId.startsWith("shape:")
      ? editor
          .getShapePageTransform(request.placement.parentId as Sheet["id"])!
          .applyToPoint(request.placement.center)
      : request.placement.center
    : undefined;
  editor.markHistoryStoppingPoint("Add drawing");
  editor.createShape<Sheet>({
    opacity: 1,
    id,
    type: "sheet",
    parentId: editor.getCurrentPageId(),
    x: center ? center.x - w / 2 : last ? last.x + 1120 + 100 : 0,
    y: center ? center.y - h / 2 : 0,
    props: {
      w,
      h,
      name: file.name.replace(/\.[^.]+$/, ""),
      source: source.src,
      preview,
      page,
      paper: "A3",
      metersPerUnit: 0,
      calibration: "Not calibrated",
      revision: "01",
    },
  });
  editor.select(id);
  editor.zoomToBounds(editor.getShapePageBounds(id)!, {
    animation: { duration: 250 },
    inset: 220,
  });
  return id;
}
export async function makeDemoPdf() {
  const pdf = await PDFDocument.create(),
    page = pdf.addPage([1120, 792]),
    font = await pdf.embedFont(StandardFonts.Helvetica),
    bold = await pdf.embedFont(StandardFonts.HelveticaBold);
  const ink = rgb(0.2, 0.25, 0.26),
    light = rgb(0.71, 0.75, 0.75);
  const line = (
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    width = 1,
    color = ink,
  ) =>
    page.drawLine({
      start: { x: x1, y: 792 - y1 },
      end: { x: x2, y: 792 - y2 },
      thickness: width,
      color,
    });
  const rect = (
    x: number,
    y: number,
    w: number,
    h: number,
    color = rgb(1, 1, 1),
    stroke = 1,
  ) =>
    page.drawRectangle({
      x,
      y: 792 - y - h,
      width: w,
      height: h,
      color,
      borderColor: ink,
      borderWidth: stroke,
    });
  const text = (
    s: string,
    x: number,
    y: number,
    size = 12,
    strong = false,
    color = ink,
  ) =>
    page.drawText(s, {
      x,
      y: 792 - y,
      size,
      font: strong ? bold : font,
      color,
    });
  text("COURTYARD HOUSE", 62, 59, 18, true);
  text("GROUND FLOOR / CONCEPT STUDY", 62, 80, 10);
  text("SYNTHETIC DEMONSTRATION DRAWING", 750, 59, 9);
  for (let x = 110; x <= 950; x += 120) line(x, 120, x, 650, 0.35, light);
  for (let y = 150; y <= 630; y += 120) line(80, y, 990, y, 0.35, light);
  rect(150, 170, 800, 420, undefined, 6);
  line(420, 170, 420, 410, 5);
  line(420, 470, 420, 590, 5);
  line(700, 170, 700, 315, 5);
  line(700, 375, 700, 590, 5);
  line(150, 380, 355, 380, 5);
  line(390, 380, 420, 380, 5);
  line(700, 380, 950, 380, 5);
  rect(465, 215, 190, 235, rgb(0.93, 0.95, 0.9), 2);
  for (let i = 0; i < 4; i++)
    page.drawCircle({
      x: 500 + (i % 2) * 110,
      y: 792 - (265 + Math.floor(i / 2) * 125),
      size: 21,
      color: rgb(0.86, 0.9, 0.82),
      borderColor: rgb(0.58, 0.67, 0.53),
      borderWidth: 0.7,
    });
  text("COURTYARD", 515, 337, 12, true);
  text("17.86 m2", 527, 357, 10);
  rect(180, 204, 108, 145, undefined, 1);
  rect(188, 212, 43, 30);
  rect(237, 212, 43, 30);
  line(180, 253, 288, 253, 0.6);
  rect(307, 201, 83, 34);
  text("BEDROOM", 316, 301, 10, true);
  rect(178, 417, 66, 100);
  rect(177, 531, 100, 31);
  rect(300, 421, 85, 137);
  text("STUDIO", 294, 491, 10, true);
  rect(754, 205, 135, 60, rgb(0.97, 0.96, 0.94));
  rect(749, 205, 12, 65);
  rect(749, 264, 145, 12);
  rect(790, 300, 66, 35);
  text("LIVING", 805, 355, 10, true);
  rect(734, 420, 174, 32);
  rect(875, 420, 33, 134);
  rect(746, 476, 90, 50);
  text("KITCHEN / DINING", 749, 572, 10, true);
  for (let x = 150; x < 950; x += 80) {
    line(x, 166, x + 50, 166, 2, light);
    line(x, 594, x + 50, 594, 2, light);
  }
  line(150, 625, 950, 625, 0.8);
  [150, 420, 700, 950].forEach((x) => line(x, 617, x, 633, 0.8));
  text("16.00 m", 520, 645, 12);
  line(110, 170, 110, 590, 0.8);
  line(102, 170, 118, 170, 0.8);
  line(102, 590, 118, 590, 0.8);
  text("8.40 m", 65, 386, 10);
  line(62, 700, 1058, 700, 0.5, light);
  text("CALQUE", 62, 730, 13, true);
  text("Example project - for exploring tools only", 62, 749, 10);
  text("A-101", 883, 730, 20, true);
  text("REV 01   /   15 SEP 2026", 883, 750, 9);
  return new File(
    [(await pdf.save()) as Uint8Array<ArrayBuffer>],
    "Courtyard house - ground floor.pdf",
    { type: "application/pdf" },
  );
}
export async function seedDemo(editor: Editor, room: string) {
  const id = await importDrawing(editor, room, await makeDemoPdf());
  editor.updateShape<Sheet>({
    id,
    type: "sheet",
    props: {
      metersPerUnit: 0.02,
      calibration: "Example: 800 drawing units = 16.00 m",
    },
  });
  editor.createShape<Mark>({
    type: "mark",
    parentId: id,
    x: 0,
    y: 0,
    props: {
      kind: "dimension",
      points: [
        { x: 150, y: 145 },
        { x: 420, y: 145 },
      ],
      color: "#d65331",
      weight: 1.5,
    },
  });
  editor.createShape<Mark>({
    type: "mark",
    parentId: id,
    x: 0,
    y: 0,
    props: {
      kind: "area",
      points: [
        { x: 465, y: 215 },
        { x: 655, y: 215 },
        { x: 655, y: 450 },
        { x: 465, y: 450 },
      ],
      color: "#657a4c",
      weight: 1.5,
    },
  });
  editor.select(id);
  return id;
}
function color(hex: string) {
  const h = /^#[\da-f]{6}$/i.test(hex) ? hex : "#d65331";
  return rgb(
    parseInt(h.slice(1, 3), 16) / 255,
    parseInt(h.slice(3, 5), 16) / 255,
    parseInt(h.slice(5, 7), 16) / 255,
  );
}
export async function exportDrawing(
  editor: Editor,
  sheet: Sheet,
  paper: "A3" | "A4",
  scale: number,
  landscape: boolean,
  options: { signal?: AbortSignal; returnBytes?: boolean } = {},
) {
  options.signal?.throwIfAborted();
  const metrics = drawingExportLayout(sheet, paper, scale, landscape);
  if (!metrics.fits)
    throw new Error(
      "The drawing does not fit at this scale. Choose a larger sheet or a larger scale denominator.",
    );
  const pdf = await PDFDocument.create(),
    page = pdf.addPage([metrics.pageW * pt, metrics.pageH * pt]),
    font = await pdf.embedFont(StandardFonts.Helvetica);
  const factor = metrics.mmPerUnit * pt,
    ox = (metrics.pageW * pt - sheet.props.w * factor) / 2,
    oy = (metrics.pageH - metrics.topMarginMm) * pt;
  page.pushOperators(
    pushGraphicsState(),
    moveTo(ox, oy),
    lineTo(ox + sheet.props.w * factor, oy),
    lineTo(ox + sheet.props.w * factor, oy - sheet.props.h * factor),
    lineTo(ox, oy - sheet.props.h * factor),
    closePath(),
    clip(),
    endPath(),
  );
  page.drawRectangle({
    x: ox,
    y: oy - sheet.props.h * factor,
    width: sheet.props.w * factor,
    height: sheet.props.h * factor,
    color: pdfColor(sheet.meta.backgroundColor),
  });
  if (sheet.props.source) {
    const res = await fetch(sheet.props.source, { signal: options.signal });
    if (!res.ok) throw new Error("Cannot read the source drawing.");
    const bytes = new Uint8Array(await res.arrayBuffer());
    if (res.headers.get("content-type")?.includes("pdf")) {
      const source = await PDFDocument.load(bytes),
        original = source.getPage(sheet.props.page - 1);
      if (original.getRotation().angle % 360 !== 0)
        throw new Error(
          "Export of rotated PDF pages is not supported yet. Upload a PDF with page rotation flattened.",
        );
      const embedded = await pdf.embedPage(original);
      page.drawPage(embedded, {
        x: ox,
        y: oy - sheet.props.h * factor,
        width: sheet.props.w * factor,
        height: sheet.props.h * factor,
      });
    } else {
      const preview = await fetch(sheet.props.preview),
        blob = await preview.blob();
      let png: Uint8Array;
      if (blob.type === "image/jpeg") {
        const image = await pdf.embedJpg(await blob.arrayBuffer());
        page.drawImage(image, {
          x: ox,
          y: oy - sheet.props.h * factor,
          width: sheet.props.w * factor,
          height: sheet.props.h * factor,
        });
        png = new Uint8Array();
      } else {
        const bitmap = await createImageBitmap(blob),
          canvas = document.createElement("canvas");
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;
        canvas.getContext("2d")!.drawImage(bitmap, 0, 0);
        bitmap.close();
        const b = await new Promise<Blob>((r) =>
          canvas.toBlob((v) => r(v!), "image/png"),
        );
        png = new Uint8Array(await b.arrayBuffer());
      }
      if (png.length) {
        const image = await pdf.embedPng(png);
        page.drawImage(image, {
          x: ox,
          y: oy - sheet.props.h * factor,
          width: sheet.props.w * factor,
          height: sheet.props.h * factor,
        });
      }
    }
  }
  const children = editor
    .getSortedChildIdsForParent(sheet.id)
    .map((id) => editor.getShape(id))
    .filter((s) => s && !editor.isShapeHidden(s));
  for (const shape of children) {
    if (!shape || shape.type !== "mark") continue;
    const transform = editor.getShapePageTransform(shape);
    exportMark(
      page,
      font,
      shape,
      sourceMeters(editor, scaleSource(editor, shape)),
      factor,
      (p) => {
        const q = editor.getPointInShapeSpace(sheet, transform.applyToPoint(p));
        return { x: ox + q.x * factor, y: oy - q.y * factor };
      },
    );
  }
  // Other annotations are exported as one transparent overlay at the exact page transform.
  const otherIds = children
    .filter((s) => s && s.type !== "mark")
    .map((s) => s!.id);
  if (otherIds.length) {
    const bounds = editor.getShapePageBounds(sheet)!,
      image = await editor.toImage(otherIds, {
        format: "png",
        background: false,
        bounds,
        padding: 0,
        // Bound the raster overlay even on large custom paper sizes (16 MP max).
        scale: Math.min(2, 4096 / Math.max(bounds.w, bounds.h)),
        pixelRatio: 1,
      });
    const embed = await pdf.embedPng(await image.blob.arrayBuffer());
    page.drawImage(embed, {
      x: ox,
      y: oy - sheet.props.h * factor,
      width: sheet.props.w * factor,
      height: sheet.props.h * factor,
    });
  }
  page.pushOperators(popGraphicsState());
  options.signal?.throwIfAborted();
  if (sheet.props.source) {
    const footer = drawingExportFooter(sheet, metrics, font);
    page.drawText(footer.text, {
      x: 10 * pt, y: 7 * pt, size: footer.size, font, color: rgb(0.3, 0.35, 0.35),
    });
  }
  const bytes = await pdf.save();
  options.signal?.throwIfAborted();
  if (options.returnBytes) return bytes;
  const blob = new Blob([bytes as Uint8Array<ArrayBuffer>], {
      type: "application/pdf",
    }),
    url = URL.createObjectURL(blob),
    a = document.createElement("a");
  a.href = url;
  a.download = `${sheet.props.name}-${paper}-1-${scale}.pdf`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
  return bytes;
}
